"use client";

import { ArrowUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Botão flutuante de voltar ao topo.
 *
 * Aparece quando a primeira seção da página (`#inicio`, o hero) deixa de estar
 * visível — na página inicial isso acontece assim que o visitante rola além do
 * hero; nas demais páginas, depois de uma tela de rolagem. A observação é
 * refeita a cada navegação para o botão não herdar o estado da rota anterior.
 *
 * O visual é vidro branco translúcido, para funcionar tanto sobre o azul-marinho
 * quanto sobre as seções claras.
 */
export function VoltarAoTopo() {
  const pathname = usePathname();
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    const hero = document.getElementById("inicio");

    if (hero && "IntersectionObserver" in window) {
      const observador = new IntersectionObserver(
        ([entrada]) => setVisivel(!entrada.isIntersecting),
        { threshold: 0 },
      );

      observador.observe(hero);
      return () => observador.disconnect();
    }

    const aoRolar = () => setVisivel(window.scrollY > window.innerHeight * 0.75);
    aoRolar();
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => window.removeEventListener("scroll", aoRolar);
  }, [pathname]);

  function voltarAoTopo(evento: React.MouseEvent<HTMLButtonElement>) {
    evento.currentTarget.blur();
    const semMovimento = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: semMovimento ? "auto" : "smooth" });
  }

  return (
    <button
      type="button"
      onClick={voltarAoTopo}
      aria-label="Voltar ao topo"
      title="Voltar ao topo"
      aria-hidden={!visivel}
      tabIndex={visivel ? 0 : -1}
      className={cn(
        "fixed right-6 top-24 z-40 flex size-12 items-center justify-center rounded-full",
        "border border-white/60 bg-white/30 text-navy shadow-lg shadow-navy/25 backdrop-blur-md",
        "transition-all duration-300 hover:-translate-y-1 hover:bg-white/60 hover:text-navy-dark",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-light",
        visivel
          ? "translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-2 opacity-0",
      )}
    >
      <ArrowUp className="size-5" aria-hidden />
    </button>
  );
}
